// frontend/src/admin/pages/AdminOrgsPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { listOrgs } from "../api";

const PER_PAGE = 50;
const PLAN_OPTIONS = ["", "Free", "Starter", "Pro", "Business"];
const STATUS_OPTIONS = ["", "active", "suspended", "deleted"];

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString();
}

/**
 * AdminOrgsPage — searchable, filterable, paginated list of organisations.
 * Each row links to the org detail page.
 */
function AdminOrgsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (q) params.q = q;
      if (plan) params.plan = plan;
      if (status) params.status = status;
      const data = await listOrgs(params);
      setRows(data.items ?? []);
      setTotal(data.total ?? 0);
      setError(null);
    } catch {
      setError("Failed to load organisations.");
    } finally {
      setLoading(false);
    }
  }, [page, q, plan, status]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900 mb-4">
        Organisations
      </h1>

      {/* Filters */}
      <form
        onSubmit={onSearchSubmit}
        className="flex flex-wrap items-center gap-3 mb-4"
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or slug…"
          aria-label="Search organisations"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm w-64"
        />
        <select
          value={plan}
          onChange={(e) => {
            setPlan(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by plan"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          {PLAN_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p || "All plans"}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by status"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-500"
        >
          Search
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No organisations found.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Plan</th>
                <th className="text-right px-4 py-2">Members</th>
                <th className="text-right px-4 py-2">Codes</th>
                <th className="text-right px-4 py-2">30d Scans</th>
                <th className="text-left px-4 py-2">Account</th>
                <th className="text-left px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/orgs/${o.id}`}
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      {o.name}
                    </Link>
                    {o.suspended_at && (
                      <span className="ml-2 text-[10px] font-bold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                        Suspended
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium bg-slate-100 text-slate-700 rounded px-2 py-0.5">
                      {o.plan_name ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">{o.user_count}</td>
                  <td className="px-4 py-2 text-right">{o.code_count}</td>
                  <td className="px-4 py-2 text-right">{o.scan_count_30d}</td>
                  <td className="px-4 py-2">
                    {o.white_account ? (
                      <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
                        White
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    {fmtDate(o.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && total > PER_PAGE && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-slate-500">
            {total} orgs · page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-slate-300 rounded-md disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 border border-slate-300 rounded-md disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrgsPage;
